import { z } from "zod";
import type { Role as DBRoleType } from "@compass/prisma";
import { ThreadAuthor } from "./author";
import { IdentifiedOrDeletedAuthor } from "./author";
import { IsoDateString } from "./isoDate";
import { Password } from "./password";
import { Post } from "./posts";
import { VALID_TIMEZONES } from "./timezone";
import { TipTapDocSchema } from "./tiptapDoc";

export { Password };

export const DBRole = {
    USER: "USER",
    COMPASS_USER: "COMPASS_USER",
    COMPASS_SUPERUSER: "COMPASS_SUPERUSER",
    READONLY_USER: "READONLY_USER",
} as const satisfies { [key in DBRoleType]: key };

export const Role = z.enum([
    DBRole.USER,
    DBRole.COMPASS_USER,
    DBRole.COMPASS_SUPERUSER,
    DBRole.READONLY_USER,
]);

export const FriendRequestStatus = z.enum(["DECLINED", "ACCEPTED"]);

export const User = z.object({
    id: z.number(),
    firstName: z.string(),
    lastName: z.string(),
    name: z.string(),
    initials: z.string().min(1).max(2),
    // Empty string for soft-deleted users (sanitizeDeletedUser) — never their real address.
    email: z.string().email().or(z.literal("")),
    avatarUrl: z.string().nullish(),
    role: Role,
    aboutMe: z.string().nullish(),
    aboutMeContentJson: TipTapDocSchema.nullable().optional(),
    interests: z.string().nullish(),
    organizationId: z.number().nullish(),
    organizationName: z.string().nullish(),
    isSocialEnabled: z.boolean(),
    isPrivateEnabled: z.boolean(),
    // Set for soft-deleted users so flat-DTO consumers can branch on state
    // (identity fields are already sanitized server-side) instead of
    // comparing against the "Deleted User" display string.
    deletedAt: IsoDateString.nullish(),
    calendarTimeZone: z
        .string()
        .refine((tz) => VALID_TIMEZONES.includes(tz), { message: "Invalid timezone" })
        .nullish(),
});

export const UserWithFriendId = z.object({
    id: z.number(),
    firstName: z.string(),
    lastName: z.string(),
    name: z.string(),
    initials: z.string().min(1).max(2),
    // Empty string for soft-deleted users (sanitizeDeletedUser) — never their real address.
    email: z.string().email().or(z.literal("")),
    avatarUrl: z.string().nullish(),
    role: Role,
    friendId: z.number(),
    deletedAt: IsoDateString.nullable(),
});

export const UserWithFriend = z.object({
    id: z.number(),
    firstName: z.string(),
    lastName: z.string(),
    name: z.string(),
    initials: z.string().min(1).max(2),
    // Empty string for soft-deleted users (sanitizeDeletedUser) — never their real address.
    email: z.string().email().or(z.literal("")),
    isSocialEnabled: z.boolean(),
    isPrivateEnabled: z.boolean(),
    avatarUrl: z.string().nullish(),
    recivedFriendRequest: z.boolean().nullable(),
    sentFriendRequest: z.boolean().nullable(),
    role: Role,
    organizationId: z.number().nullish(),
    organizationName: z.string().nullish(),
    deletedAt: IsoDateString.nullable(),
});

export const FriendRequest = z.array(
    z.object({
        id: z.number(),
        receiverId: z.number().nullish(),
        senderId: z.number().nullish(),
        // Author chip — a deleted sender renders as the tombstone.
        sender: IdentifiedOrDeletedAuthor.nullish(),
        status: z.string().nullish(),
    }),
);

export const FriendList = z.array(
    z.object({
        id: z.number(),
        userId: z.number().nullish(),
        friendId: z.number().nullish(),
        user: z.array(User).nullish(),
    }),
);

export const PublicUser = User.omit({ email: true, role: true });

export const SocialProfile = z.object({
    id: z.number(),
    firstName: z.string(),
    lastName: z.string(),
    name: z.string(),
    initials: z.string().min(1).max(2),
    // Empty string for soft-deleted users (sanitizeDeletedUser) — never their real address.
    email: z.string().email().or(z.literal("")),
    aboutMe: z.string().nullish(),
    aboutMeContentJson: TipTapDocSchema.nullable().optional(),
    interests: z.string().nullish(),
    isSocialEnabled: z.boolean(),
    deletedAt: IsoDateString.nullish(),
    avatarUrl: z.string().nullish(),
    posts: z.array(Post).nullable(),
    meta: z.object({
        bio: z.string(),
        organization: z.string(),
    }),
    postComments: z.array(
        z.object({
            id: z.number(),
            contentJson: TipTapDocSchema,
            createdAt: IsoDateString,
            updatedAt: IsoDateString,
            postId: z.number(),
            authorId: z.number(),
            deletedAt: IsoDateString.nullable(),
            post: z.object({
                id: z.number(),
                createdAt: IsoDateString,
                title: z.string(),
                updatedAt: IsoDateString,
                // Null when the post is anonymous to the viewer (PRTL-1384).
                authorId: z.number().nullable(),
                deletedAt: IsoDateString.nullable(),
                author: ThreadAuthor,
            }),
            author: ThreadAuthor,
        }),
    ),
});

/** PRTL-1348: one org the current user belongs to (multi-org accounts). */
export const OrganizationMembershipSummary = z.object({
    organizationId: z.number(),
    organizationName: z.string(),
});
export type OrganizationMembershipSummary = z.infer<typeof OrganizationMembershipSummary>;

export const CurrentUser = User.extend({
    isCompassUser: z.boolean(),
    /** PRTL-1348: the org this session's token is scoped to (mirrors organizationId during the transition). */
    activeOrganizationId: z.number().nullish(),
    /** PRTL-1348: all active memberships. */
    memberships: z.array(OrganizationMembershipSummary).optional().default([]),
    /** PRTL-1348 masquerade: set when this session is a compass admin viewing as the user. */
    masquerade: z
        .object({
            // Author-chip union per repo convention — a soft-deleted actor
            // renders as the tombstone, never a leaked name.
            actor: IdentifiedOrDeletedAuthor,
        })
        .nullish(),
    follows: z.array(User).nullable(),
    meta: z.object({
        bio: z.string(),
        organization: z.string(),
    }),
    friends: z.array(UserWithFriendId),
    sentFriendRequest: FriendRequest,
    receivedFriendRequests: FriendRequest,
    isLockedUntilReset: z.boolean().optional().default(false),
    twoFactorEnabled: z.boolean().optional().default(false),
    isSocialEnabled: z.boolean().optional().default(false),
    isPrivateEnabled: z.boolean().optional().default(true),
});

export const UserMetaDataSchema = z.object({
    bio: z.string().nullable(),
    organization: z.string().nullable(),
});

export const UserMeta = z.object({
    id: z.number(),
    key: z.string(),
    data: UserMetaDataSchema,
    userId: z.number(),
    user: CurrentUser,
});

export type UserMetaData = z.infer<typeof UserMetaDataSchema>;
